import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Columns2, Info, Loader2, RotateCcw, Save } from 'lucide-react';

import { Button } from '../Common/button';
import { ConfirmationModal } from '../Common/ConfirmationModal';
import { cn } from '../../lib/utils';
import notify from '../../lib/notify';
import aiPromptService, { describeApiError } from '../../services/aiPromptService';
import { EffectiveAnalysisLimitsCard } from '../AnalysisLimits/EffectiveAnalysisLimitsCard';
import { formatDateTime } from '../Projects/projectFormat';
import { PROJECT_PROMPT_KEYS, type AiPrompt, type PromptLocale } from '../../types/projectAnalysis';

const LOCALES: PromptLocale[] = ['fr', 'en'];

/** i18n id of a prompt key: "project.analysis.system" -> "analysisSystem". */
const promptI18nId = (key: string): string =>
  key
    .replace(/^project\./, '')
    .split(/[._]/)
    .map((part, i) => (i === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)))
    .join('');

const draftKey = (key: string, locale: PromptLocale) => `${key}|${locale}`;

interface ProjectPromptsTabProps {
  /** The organization whose prompts are edited (the admin's own; God's current one). */
  organizationId?: string | null;
}

/**
 * « Prompts Projets »: the prompts of the project analysis, per language, with
 * the code default as reference. The JSON output contract is appended by the
 * code, so it is neither shown nor editable here.
 */
export const ProjectPromptsTab: React.FC<ProjectPromptsTabProps> = ({ organizationId }) => {
  const { t, i18n } = useTranslation();
  const [prompts, setPrompts] = useState<AiPrompt[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string>(PROJECT_PROMPT_KEYS[0]);
  const [locale, setLocale] = useState<PromptLocale>(i18n.language?.startsWith('en') ? 'en' : 'fr');
  // Unsaved edits per prompt and language, kept while switching.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [showDefault, setShowDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setPrompts(await aiPromptService.list(organizationId));
    } catch (error) {
      setLoadError(describeApiError(error, t('knowledgeManagement.projectPrompts.loadError')));
    }
  }, [organizationId, t]);

  useEffect(() => {
    setPrompts(null);
    setDrafts({});
    void load();
  }, [load]);

  // Known keys first, in pipeline order; any other key the backend serves after.
  const ordered = useMemo(() => {
    if (!prompts) return [];
    const rank = (k: string) => {
      const i = (PROJECT_PROMPT_KEYS as readonly string[]).indexOf(k);
      return i === -1 ? PROJECT_PROMPT_KEYS.length : i;
    };
    return [...prompts].sort((a, b) => rank(a.key) - rank(b.key));
  }, [prompts]);

  const prompt = ordered.find((p) => p.key === selectedKey) ?? ordered[0];
  const version = prompt?.versions.find((v) => v.locale === locale);
  const stored = version ? version.override ?? version.default : '';
  const dk = prompt ? draftKey(prompt.key, locale) : '';
  const value = dk in drafts ? drafts[dk] : stored;
  const dirty = dk in drafts && drafts[dk] !== stored;

  useEffect(() => {
    setSaveError(null);
  }, [dk]);

  const setValue = (next: string) => setDrafts((prev) => ({ ...prev, [dk]: next }));

  const dropDraft = (key: string) =>
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const replacePrompt = (updated: AiPrompt) =>
    setPrompts((prev) => prev?.map((p) => (p.key === updated.key ? updated : p)) ?? [updated]);

  const insertPlaceholder = (name: string) => {
    const token = `{${name}}`;
    const el = textareaRef.current;
    if (!el) {
      setValue(value + token);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    setValue(value.slice(0, start) + token + value.slice(end));
    window.requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const save = async () => {
    if (!prompt || !value.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      replacePrompt(await aiPromptService.save(prompt.key, locale, value, organizationId));
      dropDraft(dk);
      notify.success(t('knowledgeManagement.projectPrompts.saved'));
    } catch (error) {
      // 400 names the unknown placeholders: keep it next to the editor.
      const message = describeApiError(error, t('knowledgeManagement.projectPrompts.saveError'));
      setSaveError(message);
      notify.error(message);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!prompt) return;
    setSaving(true);
    try {
      replacePrompt(await aiPromptService.reset(prompt.key, locale, organizationId));
      dropDraft(dk);
      setConfirmReset(false);
      notify.success(t('knowledgeManagement.projectPrompts.resetDone'));
    } catch (error) {
      notify.error(describeApiError(error, t('knowledgeManagement.projectPrompts.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const header = (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="text-base font-semibold">{t('knowledgeManagement.projectPrompts.title')}</h3>
      <p className="mt-1 text-xs text-muted-foreground">{t('knowledgeManagement.projectPrompts.description')}</p>
      <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        {t('knowledgeManagement.projectPrompts.jsonContract')}
      </p>
    </div>
  );

  if (loadError) {
    return (
      <div className="space-y-4">
        {header}
        <p className="text-sm text-destructive">{loadError}</p>
      </div>
    );
  }

  if (!prompts || !prompt) {
    return (
      <div className="space-y-4">
        {header}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('knowledgeManagement.projectPrompts.loading')}
        </div>
      </div>
    );
  }

  const id = promptI18nId(prompt.key);
  const overridden = version?.override != null;

  return (
    <div className="space-y-4">
      {header}

      <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
        <nav aria-label={t('knowledgeManagement.projectPrompts.listLabel')} className="space-y-1.5">
          {ordered.map((p) => {
            const pid = promptI18nId(p.key);
            const custom = p.versions.some((v) => v.locale === locale && v.override != null);
            const pendingDraft = Object.keys(drafts).some((k) => k.startsWith(`${p.key}|`));
            const selected = p.key === prompt.key;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => setSelectedKey(p.key)}
                aria-current={selected ? 'true' : undefined}
                className={cn(
                  'w-full rounded-lg border px-3 py-2.5 text-left transition-colors',
                  selected ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-muted/50'
                )}
              >
                <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                  {t(`knowledgeManagement.projectPrompts.keys.${pid}.title`, { defaultValue: p.key })}
                  {pendingDraft && (
                    <span
                      className="h-1.5 w-1.5 rounded-full bg-amber-500"
                      title={t('knowledgeManagement.projectPrompts.unsaved')}
                    />
                  )}
                </span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {t(`knowledgeManagement.projectPrompts.keys.${pid}.description`, { defaultValue: '' })}
                </span>
                {custom && (
                  <span className="mt-1.5 inline-block rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary">
                    {t('knowledgeManagement.projectPrompts.customized')}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <section className="min-w-0 space-y-3 rounded-lg border border-border bg-card p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-foreground">
                {t(`knowledgeManagement.projectPrompts.keys.${id}.title`, { defaultValue: prompt.key })}
              </h4>
              <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">{prompt.key}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {overridden
                  ? version?.updated_at
                    ? t('knowledgeManagement.projectPrompts.modifiedOn', { date: formatDateTime(version.updated_at) })
                    : t('knowledgeManagement.projectPrompts.customized')
                  : t('knowledgeManagement.projectPrompts.usingDefault')}
              </p>
            </div>
            <div
              role="radiogroup"
              aria-label={t('knowledgeManagement.projectPrompts.language')}
              className="flex rounded-lg border border-border bg-muted/40 p-0.5"
            >
              {LOCALES.map((l) => (
                <button
                  key={l}
                  type="button"
                  role="radio"
                  aria-checked={locale === l}
                  onClick={() => setLocale(l)}
                  className={cn(
                    'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                    locale === l ? 'bg-background text-foreground shadow' : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {t(`knowledgeManagement.projectPrompts.locale.${l}`)}
                </button>
              ))}
            </div>
          </div>

          {prompt.placeholders.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground">{t('knowledgeManagement.projectPrompts.placeholders')}</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {prompt.placeholders.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => insertPlaceholder(name)}
                    title={t('knowledgeManagement.projectPrompts.insertPlaceholder')}
                    className="rounded-md border border-border bg-muted/50 px-2 py-0.5 font-mono text-[12px] text-foreground hover:border-primary hover:text-primary"
                  >
                    {`{${name}}`}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className={cn('grid gap-3', showDefault && 'xl:grid-cols-2')}>
            <div className="min-w-0">
              {showDefault && (
                <p className="mb-1 text-xs font-medium text-muted-foreground">
                  {t('knowledgeManagement.projectPrompts.yourVersion')}
                </p>
              )}
              <textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                rows={18}
                spellCheck={false}
                disabled={saving}
                aria-label={t('knowledgeManagement.projectPrompts.editorLabel')}
                aria-invalid={!!saveError}
                className={cn(
                  'w-full resize-y rounded-md border bg-background px-3 py-2 font-mono text-[13px] leading-relaxed text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  saveError ? 'border-destructive' : 'border-input'
                )}
              />
            </div>
            {showDefault && (
              <div className="min-w-0">
                <p className="mb-1 text-xs font-medium text-muted-foreground">
                  {t('knowledgeManagement.projectPrompts.defaultVersion')}
                </p>
                <pre className="max-h-[27rem] overflow-auto whitespace-pre-wrap rounded-md border border-dashed border-border bg-muted/30 px-3 py-2 font-mono text-[13px] leading-relaxed text-muted-foreground">
                  {version?.default ?? ''}
                </pre>
              </div>
            )}
          </div>

          {saveError && (
            <p
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive"
            >
              {saveError}
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5"
              onClick={() => setShowDefault((v) => !v)}
              aria-pressed={showDefault}
            >
              <Columns2 className="h-4 w-4" />
              {showDefault
                ? t('knowledgeManagement.projectPrompts.hideDefault')
                : t('knowledgeManagement.projectPrompts.showDefault')}
            </Button>
            <div className="flex flex-wrap gap-2">
              {dirty && (
                <Button variant="outline" size="sm" onClick={() => dropDraft(dk)} disabled={saving}>
                  {t('knowledgeManagement.projectPrompts.discard')}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setConfirmReset(true)}
                disabled={saving || !overridden}
              >
                <RotateCcw className="h-4 w-4" />
                {t('knowledgeManagement.projectPrompts.reset')}
              </Button>
              <Button
                size="sm"
                className="gap-1.5"
                onClick={() => void save()}
                disabled={saving || !dirty || !value.trim()}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {t('knowledgeManagement.projectPrompts.save')}
              </Button>
            </div>
          </div>
        </section>
      </div>

      <EffectiveAnalysisLimitsCard organizationId={organizationId} />

      <ConfirmationModal
        open={confirmReset}
        variant="warning"
        title={t('knowledgeManagement.projectPrompts.resetTitle')}
        message={t('knowledgeManagement.projectPrompts.resetMessage', {
          language: t(`knowledgeManagement.projectPrompts.locale.${locale}`),
        })}
        confirmText={t('knowledgeManagement.projectPrompts.reset')}
        cancelText={t('global.cancel')}
        onConfirm={() => void reset()}
        onCancel={() => setConfirmReset(false)}
        loading={saving}
      />
    </div>
  );
};

export default ProjectPromptsTab;
